import CopyIcon from "@atlaskit/icon/glyph/copy";
import styled from "@emotion/styled";
import prop from "ramda/src/prop";
import { useEffect, useRef, useState } from "react";
import { L2Selector, L3Selector, L4Selector } from "../../common/network/NetworkSelectors";
import { ModalProvider } from "../../contexts/ModalContext";
import { copyToClipboard } from "../../helpers/copyToClipboard";
import { useNetwork } from "../../hooks/useNetwork";
import { useSelectedNetworkImportTypes } from "../../hooks/useSelectedNetworkImportType";
import { useSnackbar } from "../../hooks/useSnackbar";
import useAuth from "../../routes/login/AuthContext";
import { GetInvitationForNetwork } from "../../services/NetworkApi";
import { Button } from "../../UI/Button";
import { Grid } from "../../UI/Grid";
import { Link } from "../../UI/Link";
import { Paper } from "../../UI/Paper";
import { Select } from "../../UI/Select";
import { TextField } from "../../UI/TextField";
import { Typography } from "../../UI/Typography";
import { SelectWrapper } from "../SelectWrapper";
import EmailTemplate from "./EmailTemplate";
import { GeneralInvitationCreation } from "./GeneralInvitationCreation";



const propValue = prop("value");

const RENEW_OPTION_TOOLTIP_CONTENT =
  "A renewing resident is anyone who already lives at your community and is staying in their same room for the upcoming lease term. Renewing residents cannot match themselves with other renewing residents in RoomSync.  This is to prevent the creation of unapproved renewal transfers. Renewing residents will only be able to see and match with new residents whereas new residents can see and match with both new and renewing residents. You as the admin can always bypass this restriction and match renewing residents together.";

const COMMUNITY_TYPE_OPTIONS = [
  { label: "By-the-Bed", value: "by_the_bed" },
  { label: "Joint and Several", value: "joint_lease" },
];

const RENEWAL_OPTIONS = [
  { label: "New Resident Link", value: "new_resident" },
  { label: "Renewing Resident Link", value: "renewing_resident" },
];

const FINAL_SET_OPTIONS = [
  { label: "We receive a matching report from RoomSync", value: "auto" },
  {
    label: "Students self-report matches via housing portal",
    value: "self_report",
  },
];

const StyledCopyButton = styled(Button)`
  min-width: 119px;
`;

export const UserInviteByLink = () => {
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const { selectedNetwork, getL2, getL3 } = useNetwork();
  const { isUniquePassCode } = useSelectedNetworkImportTypes();
  const { getAuthToken } = useAuth();

  const [invitations, setInvitations] = useState({
    invitations: [],
    load: true,
  });
  const [selectedCommunityType, setSelectedCommunityType] = useState(null);
  const [selectedRenewalOption, setSelectedRenewalOption] = useState(null);
  const [selectFinalStep, setSelectFinalStep] = useState(null);

  const [subnetwork, setSubnetwork] = useState(null);
  const [l3, setL3] = useState(null);
  const [l4, setL4] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      if (invitations.load && selectedNetwork.l1Id !== 0) {
        try {
          const invitationResponse = await GetInvitationForNetwork(selectedNetwork.l1Id, await getAuthToken());
          if (invitationResponse)
            setInvitations({
              invitations: invitationResponse.invitations,
              load: false,
            });
        } catch (error) {
          showErrorSnackbar(error);
        }
      }
    };

    load();
  }, [selectedNetwork.l1Id, invitations.load]);

  useEffect(() => {
    setL3(null);
    setL4(null);
  }, [subnetwork]);

  useEffect(() => {
    setL4(null);
  }, [l3]);

  let shouldDraw = subnetwork && l3;

  if (l3) {
    const l3Info = getL3(selectedNetwork.l1Id, subnetwork, l3);

    if (l3Info && l3Info.l4 && l3Info.l4.length > 0) {
      shouldDraw = shouldDraw && l4;
    }
  }

  if (selectedNetwork.apt) {
    shouldDraw = shouldDraw && propValue(selectedCommunityType) && propValue(selectedRenewalOption);
  } else {
    shouldDraw = shouldDraw && propValue(selectFinalStep);
  }

  const currentNetwork = getL2(selectedNetwork.l1Id, subnetwork);

  const invitationBySubnetwork = invitations.invitations[subnetwork];
  const invitationByUnit = invitationBySubnetwork ? invitationBySubnetwork[l3] : undefined;
  const invitationByRoom = invitationByUnit ? invitationByUnit[l4 || 0] : undefined;
  const renewal = propValue(selectedRenewalOption) === "renewing_resident" ? "RENEWING" : "NEW";
  const invitation = invitationByRoom ? invitationByRoom[renewal] : undefined;

  const reloadInvitations = () => setInvitations((prevState) => ({ ...prevState, load: true }));

  const handleCopy = () => {
    copyToClipboard(invitation.url);
    showSuccessSnackbar("Successfully copied to clipboard");
  };

  const handleOpenModal = () => {
    modalRef.current && modalRef.current.openModal();
  };

  const l2Filter = (l2) => (l2.matchingRoundStatus !== "ARCHIVED" && l2.matchingRoundStatus !== "CLOSED");

  return (
    <Grid container spacing={2}>
      <Grid item>
        <SelectWrapper>
          <Select
            label="Community Type"
            value={selectedCommunityType}
            inputId="community-type-select"
            options={COMMUNITY_TYPE_OPTIONS}
            onChange={setSelectedCommunityType}
          />
        </SelectWrapper>
      </Grid>
      <Grid item>
        <SelectWrapper>
          <Select
            label="Renewing Resident"
            options={RENEWAL_OPTIONS}
            value={selectedRenewalOption}
            inputId="renewal-resident-select"
            labelHelpTooltop={RENEW_OPTION_TOOLTIP_CONTENT}
            onChange={setSelectedRenewalOption}
          />
        </SelectWrapper>
      </Grid>
      <Grid item>
        <L2Selector 
          onSelectHandler={setSubnetwork}
          filter={l2Filter}
        />
      </Grid>
      <Grid item>
        <L3Selector onSelectHandler={setL3} l2Selection={subnetwork} />
      </Grid>
      {l3 &&
        getL3(selectedNetwork.l1Id, subnetwork, l3) &&
        getL3(selectedNetwork.l1Id, subnetwork, l3).l4 &&
        getL3(selectedNetwork.l1Id, subnetwork, l3).l4.length > 0 && (
          <Grid item>
            <L4Selector onSelectHandler={setL4} l2Selection={subnetwork} l3Selection={l3} />
          </Grid>
        )}
      {!selectedNetwork.apt && (
        <Grid item>
          <SelectWrapper>
            <Select
              value={selectFinalStep}
              label="How matches are reported"
              inputId="how-matches-are-reported-select"
              options={FINAL_SET_OPTIONS}
              onChange={setSelectFinalStep}
            />
          </SelectWrapper>
        </Grid>
      )}
      {shouldDraw && (
        <Grid item xs={12} mt={3}>
          <ModalProvider>
            <Paper sx={{ p: 3, mb: "20px" }}>
              <Typography variant="h6">RoomSync Invite Link</Typography>
              {invitation && (
                <>
                  <TextField
                    isReadOnly
                    value={invitation.url}
                    elemAfterInput={
                      <StyledCopyButton onClick={handleCopy} iconBefore={<CopyIcon label="copy link" />}>
                        Copy Link
                      </StyledCopyButton>
                    }
                  />
                  <Typography variant="subtitle1" gutterBottom style={{ marginTop: 10, fontWeight: "bold" }}>
                    This is your invitation link for all residents with the status and unit type in the dropdown boxes
                    above. Once a resident uses the link to join and choose a login method, that resident should not click
                    it again.
                  </Typography>
                  <Typography variant="subtitle1">
                    If needed, you can permanently deactivate this link and generate a new one{" "}
                    <Link style={{ cursor: "pointer" }} onClick={handleOpenModal}>
                      here
                    </Link>
                    .
                  </Typography>
                </>
              )}
              {!invitation && (
                <>
                  <Typography variant="subtitle1" gutterBottom style={{ marginTop: 10 }}>
                    No invitation link has been generated yet for the selected options. Click the button below to
                    generate a new invitation link.
                  </Typography>
                  <Button appearance="primary" onClick={handleOpenModal} style={{ marginTop: 10 }}>
                    Generate Link
                  </Button>
                </>
              )}
              <GeneralInvitationCreation
                l1Id={selectedNetwork.l1Id}
                l2Id={subnetwork}
                l3Id={l3}
                l4Id={l4 || 0}
                invitationId={invitation?.id}
                renewal={renewal === "RENEWING"}
                reloadInvitations={reloadInvitations}
                ref={modalRef}
              />
            </Paper>
          </ModalProvider>
          {invitation && (
            <EmailTemplate
              l2Id={subnetwork}
              l3Id={l3}
              l4Id={l4 || 0}
              networkName={currentNetwork.l1Name}
              isPassCode={currentNetwork.passCodeEnabled}
              isUniquePassCode={isUniquePassCode}
              isJointLease={propValue(selectedCommunityType) === "joint_lease"}
              isApt={currentNetwork.apt}
              finalStepRequired={propValue(selectFinalStep) === "self_report"}
              invitationUrl={invitation.url}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};
